import axios from "axios";
import { Buffer } from "buffer";
import { toast } from "react-toastify";
import { getEos } from "./eosconfig";
import ecc from "alaiojs-ecc";
import * as aladinjs from "aladinjs";
import * as bip39 from "bip39";
import HDKey from "hdkey";
import wif from "wif";
import { ALACRITY_URL, CONTRACT_NAME, PROD_ENVIRONMENT } from "../config/misc";
import ToastifyIconComponent from "../components/Common/ToastifyIconComponent.jsx"
import { readJson } from "../utils/safeJson";

const hyperion = PROD_ENVIRONMENT
  ? ALACRITY_URL
  : "https://hypertestapi.alacritys.net/";

export const checkAlacrityUsername = async (name) => {
  const { eosapi } = getEos();
  let isUserNameExists = false;

  await eosapi
    .getAccount(name)
    .then((res) => {
      isUserNameExists = true;
    })
    .catch((err) => {
      isUserNameExists = false;
    });

  return isUserNameExists;
};

export const isValidMnemonic = async (name, publicKey) => {
  const { eosapi } = getEos();
  let isValid = false;
  await eosapi
    .getAccount(name)
    .then((res) => {
      if (res?.permissions[0]?.required_auth?.keys[0]?.key === publicKey) {
        isValid = true;
      }
    })
    .catch((err) => {
      isValid = false;
    });

  return isValid;
};

export const generateKeysByMnemonic = async (mnemonic) => {
  const seed = Buffer?.from(mnemonic)?.toString("hex");
  const privateKey = await window?.alaexplorerjs_ecc?.seedPrivate(seed);
  const publicKey = await window?.alaexplorerjs_ecc?.privateToPublic(
    privateKey
  );
  console.log("------------", mnemonic);

  return {
    mnemonic,
    privateKey,
    publicKey,
  };
};

export const getUserKeys = (mnemonicCode, password) => {
  return new Promise((res, rej) => {
    if (!mnemonicCode && !password) {
      console.log("NOT SIGNED");
      res(false);
    }

    axios
      .post("https://hypertest.alacritys.net/users/getKeys", {
        mnemonicCode,
        password,
      })
      .then((data) => {
        console.log({ data });
        res(data);
      })
      .catch((err) => {
        console.log({ err });
        rej(err);
      });
  });
};

export const getKeys = async (mnemonic, password) => {
  try {
    //when account locked request hit no mnemonic will found, so we have to decrypt it by fetching it from local storage
    if (!mnemonic) {
      const alacrityStorage = readJson("alacrity", null);
      mnemonic = await decryptMnemonic(alacrityStorage?.mnemonic, password);
    }
    let active = await getPublicPrivateKeyFromMnemonic(mnemonic, 0, "owner");
    let owner = await getPublicPrivateKeyFromMnemonic(mnemonic, 1000, "");

    return { active, owner };
  } catch (err) {
    console.log("getKeys error:", err);
  }
};

const getPublicPrivateKeyFromMnemonic = async (mnemonic, accno, parent) => {
  const seed = await bip39.mnemonicToSeedSync(mnemonic);
  const master = await HDKey.fromMasterSeed(Buffer.from(seed));
  const node = await master.derive("m/44'/194'/0'/0/" + accno + "");
  const pub_key = await ecc.PublicKey(node._publicKey).toString();
  const priv_key = await wif.encode(128, node._privateKey, false);
  const respObj = {
    priv_key: priv_key,
    pub_key: pub_key,
    parent,
  };
  return respObj;
};

export const encryptMnemonic = async (mnemonic, password) => {
  const passwordPromise = new Promise((resolve, reject) => {
    let pass = "";
    pass = password;
    resolve(pass);
  });

  return passwordPromise
    .then((pass) => aladinjs.encryptMnemonic(mnemonic, pass))
    .then((cipherTextBuffer) => {
      return cipherTextBuffer.toString("base64");
    })
    .catch((e) => {
      return null;
    });
};

const decryptMnemonic = async (mnemonicCode, password) => {
  try {
    const ciphertext = mnemonicCode;
    const passwordPromise = new Promise((res, rej) => {
      const pass = password;
      res(pass);
    });
    return passwordPromise
      .then((pass) =>
        aladinjs.decryptMnemonic(Buffer.from(ciphertext, "base64"), pass)
      )
      .catch((error) => {
        return null;
      });
  } catch (error) {
    console.error( error);
    return null
  }
};

export const getUsername = async (public_key) => {
  // let api = PROD_ENVIRONMENT
  //   ? `${hyperion}/v1/history/get_key_accounts`
  //   : `${hyperion}/v2/state/get_key_accounts`;
  let api = `${hyperion}/v1/history/get_key_accounts`;
  // let api =  `https://alaio/v2/state/get_key_accounts`;
  //uncomment when use hyperion2.alacrity.net
  // public_key = public_key.replace("ALA", "EOS");
  const config = {
    headers: {
      "Content-Type": "text/plain",
      Accept: "*/*",
    },
  };

  try {
    return await axios.post(api, { public_key }, config);
  } catch (err) {
    console.error("Exception at Get Alacrity Username", err);
    return null;
  }
};

export const login = async (password, mnemonic) => {
  const res = await getKeys(mnemonic, password);

  const alacrityStorage = readJson("alacrity", null);

  if (res) {
    //return when public key stored in storage is not found on unlocking account
    if (
      alacrityStorage &&
      alacrityStorage?.owner_pb_key !== res?.owner?.pub_key
    ) {
      toast.error(<div className="content">The password you've entered is incorrect. Please try again or logout and login again using your mnemonic.</div>, {
        icon: () => (<ToastifyIconComponent />),
        className: "midl-toast",
      });
      return null;
    }

    if (mnemonic) mnemonic = await encryptMnemonic(mnemonic, password);

    const activePrivateKey = res?.active?.priv_key;
    const ownerPrivateKey = res?.owner?.priv_key;
    const username = await getUsername(res?.owner?.pub_key);

    if (username && username.data?.account_names.length > 0) {
      //save to local storage to compare it when account is locked
      if (!alacrityStorage) {
        localStorage.setItem(
          "alacrity",
          JSON.stringify({
            owner_pb_key: res?.owner?.pub_key,
            active_pb_key: res?.active?.pub_key,
            mnemonic: mnemonic,
          })
        );
      }

      //sign message
      const message = `To prove you have access to this wallet,Sign this message`;
      const signature = ecc.sign(message, ownerPrivateKey);
      return {
        privateKey: activePrivateKey,
        username: username.data?.account_names[0],
        publicKey: res?.owner?.pub_key,
        signature,
      };
    } else {
      toast.error(<div className="content">Unable to find your account</div>, {
        icon: () => (<ToastifyIconComponent />),
        className: "midl-toast",
      });
      return null;
    }
  } else {
    toast.error(<div className="content">Unable to find your account</div>, {
      icon: () => (<ToastifyIconComponent />),
      className: "midl-toast",
    });
    return null;
  }
};

export const generateKeys = async () => {
  const mnemonic = await window?.bip39?.generateMnemonic();
  const seed = Buffer?.from(mnemonic)?.toString("hex");
  const privateKey = await window?.alaexplorerjs_ecc?.seedPrivate(seed);
  const publicKey = await window?.alaexplorerjs_ecc?.privateToPublic(
    privateKey
  );

  if (!privateKey?.length || !publicKey?.length || !mnemonic?.length) {
    generateKeys();
  }
  return {
    mnemonic,
    privateKey,
    publicKey,
  };
};

export const createAlacrityAcount = async (name, ownerKey, userId) => {
  let payload = JSON.stringify({
    alacrityName: name,
    publicKey: ownerKey,
    userId,
  });

  let config = {
    method: "post",
    url: "https://api-dev.midl.finance/user/createAlacrityAccount",
    headers: {
      "Content-Type": "application/json",
    },
    data: payload,
  };

  let isAccountCreated = false;

  await axios(config)
    .then((response) => {
      if (response?.data?.success) {
        isAccountCreated = true;
      } else {
        isAccountCreated = false;
        toast.error(<div className="content">{response?.data?.message}</div>, {
          icon: () => (<ToastifyIconComponent />),
          className: "midl-toast",
        });
      }
    })
    .catch((error) => {
      isAccountCreated = false;
    });

  return isAccountCreated;
};

export const getUserOpns = async (name, rpc) => {
  const { eosapi } = rpc;
  const opns = await eosapi?.getCurrencyBalance("opn", name, "OPN");
  let totalOpns = 0;
  if (opns?.length > 0) {
    totalOpns = Number(opns[0]?.split(" ")[0]);
  }
  return totalOpns;
};

export const getWithdrawLimit = async (name, rpc, tokenName, curr) => {
  const { eosapi } = rpc;
  const contract = tokenName;

  try {
    const result = await eosapi.getTableRows({
      json: true,
      code: contract,
      scope: name,
      table: "accounts",
      limit: 100000,
      reverse: false,
      index_position: 0,
      key_type: "i64",
      show_payer: false,
    });

    let limit = 0;

    if (result.rows.length > 0) {
      result.rows.forEach((row) => {
        const currency = row.balance.split(" ")[1];
        const amount = parseFloat(row.balance.split(" ")[0]);
        if (currency === curr) {
          limit = amount.truncateIt();
        }
      });
    }

    return limit !== undefined ? limit : 0;
  } catch (err) {
    console.error("Exception on get Withdraw Limit", err);

    return 0;
  }
};

export const getJuryStakes = async (name, rpc) => {
  const { eosapi } = rpc;
  try {
    const res = await eosapi.getTableRows({
      code: CONTRACT_NAME,
      scope: CONTRACT_NAME,
      table: "jurystake",
      limit: 100000,
      json: true,
      lower_bound: name,
      upper_bound: name,
    });
    if (res.rows.length) {
      return parseFloat(res.rows[0].free_amount.split(" ")[0]);
    } else {
      return 0;
    }
  } catch (error) {
    console.error("Exception on get get Jury Stakes", error);
    return 0;
  }
};
export const getWrappedBalances = async (username, tokenContract, rpc) => {
  const { eosapi } = rpc;
  try {
    const res = await eosapi.getTableRows({
      code: tokenContract,
      scope: username,
      table: "accounts",
      limit: 100000,
      json: true,
    });
    if (res.rows.length) return parseFloat(res.rows[0].balance.split(" ")[0]);
    else return null;
  } catch (error) {
    console.error("Exception on getWrappedBalances", error);
    return null;
  }
};
